<template>
  <div class="ingredient-item-tile">
    <div class="tile-check__wrapper">
      <input
        v-if="selectionAvailable && canModifyCarts"
        type="checkbox"
        name="tile-checkbox"
        v-model="model.selected"
      />
      <label
        v-if="selectionAvailable && canModifyCarts"
        class="tile-check"
        for="tile-checkbox"
        @click="model.selected = !model.selected"
      ></label>
    </div>
    <header class="ingredient-item__header">
      <span class="ingredient-name">
        <div @click="onTileClick">
          <span class="ingredient-tile__title" data-cy="ingredient-tile-title">
            {{ model.LabelName }}
            {{ model.LabelNumber ? "/ " + model.LabelNumber : "" }}
          </span>
          <span class="label-dpm" data-cy="ingredient-tile-dpm"
            >[{{ dpm }}]</span
          >
        </div>
        <span class="task-list__card-info task-list__card-info--label">
          <div @click="onTileClick">
            <div class="cc-label-name" data-cy="ingredient-tile-company">
              {{ model.DistributorCompanyName }}
            </div>
            <div class="cc-label-name" data-cy="ingredient-tile-brand">
              Group {{ group }} / {{ model.Symbol }}
            </div>
            <div class="cc-label-name" v-if="model.Rmc">
              RMC: {{ model.Rmc }}
            </div>
            <div class="cc-label-name" data-cy="ingredient-tile-plantname">
              Plant: {{ model.PlantName }}
            </div>

            <div class="special-req-container">
              <span
                v-if="model._Pesach"
                class="special-req-indicator"
                data-cy="ingredient-tile-passover"
                >Passover</span
              >
              <span
                v-if="model.VueScheduleAStipulationExists"
                class="special-req-indicator"
                data-cy="ingredient-tile-comments"
                >Special Requirements</span
              >
            </div>
          </div>
        </span>
      </span>
      <span
        class="status"
        :class="{
          approved:
            model.VueScheduleAUsedInOneStatus == EnumUsedIn1Status.Approved,
        }"
      >
        {{ ingredientStatusInPlant }}
      </span>
    </header>

    <div
      v-show="showDetails"
      class="ingredient-details"
      data-cy="ingredient-details"
    >
      <Grid
        :data-items="ingredientProperties"
        :columns="columns"
        :scrollable="'none'"
      >
        <template v-slot:emptyHeader="{}"> </template>
      </Grid>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from "vue";
import { EnumDpm } from "@/Domain/EnumDpm";
import { EnumUsedIn1Status } from "@/Domain/EnumUsedIn1Status";
import type { IngredientWithSelection } from "@/api/scheduleA";
import { Grid, type GridColumnProps } from "@progress/kendo-vue-grid";
import { EnumMerchStatus } from "@/Domain/EnumMerchStatus";
import { convertNumber } from "@/Domain/EnumGroupConverter";
import { EnumGroup } from "@/Domain/EnumGroup";
import { useMainStore } from "@/stores/useMainStore";

const main = useMainStore();

defineProps<{
  canModifyCarts: boolean;
}>();

const model = defineModel<IngredientWithSelection>({ required: true });

const selectionAvailable = computed(() => {
  return !(
    model.value.VueScheduleAGroup == EnumGroup.Six &&
    (model.value.VueScheduleAUsedInOneStatus == EnumUsedIn1Status.Allowed ||
      model.value.VueScheduleAUsedInOneStatus == EnumUsedIn1Status.NotAllowed)
  );
});

const ingredientStatusInPlant = computed(() => {
  return EnumUsedIn1Status[model.value.VueScheduleAUsedInOneStatus!];
});

const showDetails = ref<boolean>(false);
const ingredientProperties = ref<{ name: string; value: string }[]>([]);

const dpm = computed(() => {
  return EnumDpm[model.value._Dpm!];
});

const group = computed(() => {
  return convertNumber(model.value._Group!);
});

const merchStatus = computed(() => {
  return EnumMerchStatus[model.value._MerchStatus!];
});

const columns: GridColumnProps[] = [
  {
    field: "name",
    headerCell: "emptyHeader",
    className: "name-row",
  },
  {
    field: "value",
    headerCell: "emptyHeader",
  },
];

const setIngredientProperties = function () {
  const rows: any[] = [];

  rows.push(
    {
      name: "Brand",
      value: model.value.VueScheduleABrandName,
    },
    {
      name: "UKD ID",
      value: model.value.VueScheduleAAgencyId,
    },
    {
      name: "Ingredient Status",
      value: merchStatus,
    },
    {
      name: "Merchandise ID",
      value: model.value.MerchandiseId,
    }
  );

  if (model.value.VueMerchAndLabelCommentsScheduleA?.length)
    rows.push({
      name: "Special Requirements",
      value: model.value.VueMerchAndLabelCommentsScheduleA,
    });

  if (model.value.VueScheduleASealSign?.replace(/\s/g, "")?.length)
    rows.push({
      name: "Signature or Seal Sign",
      value: model.value.VueScheduleASealSign,
    });

  if (model.value.PlantSpecificComment?.replace(/\s/g, "")?.length)
    rows.push({
      name: "Plant Specific Comment",
      value: model.value.PlantSpecificComment,
    });

  if (main.isEmulator) {
    rows.push({
      name: "Certified Until",
      value: model.value.CertifiedUntil
        ? new Date(model.value.CertifiedUntil).toLocaleDateString()
        : "",
    });
  }

  ingredientProperties.value = rows;
};

setIngredientProperties();

const onTileClick = function (e: MouseEvent) {
  const el = e.target as Element;
  if (el && el.tagName.toLowerCase() === "a") return;
  showDetails.value = !showDetails.value;
};

watchEffect(() => {
  setIngredientProperties();
});
</script>

<style lang="scss" scoped>
.k-grid-header {
  display: none !important;
}

.k-grid {
  border: 0px;
  .k-grid-header {
    display: none;
  }
}

.ingredient-item-tile {
  background: #fff;
  margin-bottom: 10px;
  border-radius: 4px;
  padding: 20px 15px;
  padding-left: 65px;
  cursor: pointer;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  position: relative;

  .name {
    color: #1f262e;
    font-size: 14px;
    font-weight: 500;
  }

  .task-list__card-info--label {
    color: #a5a5a5;
    font-size: 12px;
    margin-top: 6px;
    display: block;
  }

  .ingredient-details {
    margin-top: 12px;

    .k-grid {
      border: 0;
    }

    .k-grid-header {
      display: none !important;
    }
  }

  .special-req-container {
    display: flex;
    gap: 12px;
  }

  .special-req-indicator {
    display: inline-block;
    margin-top: 5px;
    background: #666;
    color: white;
    font-size: 11px;
    padding: 3px 7px;
  }

  .label-dpm {
    display: inline-block;
    font-size: 12px;
    margin-left: 5px;
    color: #999;
  }

  .tile-check__wrapper {
    input {
      position: absolute;
      left: -999px;
    }
    label.tile-check {
      width: 15%;
      max-width: 60px;
      height: 100%;
      background: #f7f7f7;
      border: 1px solid #e9edef;
      border-right: 1px solid #efefef;
      border-radius: 4px 0 0 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 10px;
      position: absolute;
      left: 0;
      top: 0;
    }

    input:checked ~ label {
      background: #428bca;
      border-color: #428bca;
    }

    input:checked ~ label:before {
      content: "✓";
      color: white;
      font-size: 20px;
    }
  }

  .status {
    position: absolute;
    top: 0;
    right: 0;
    border-radius: 0 6px 0 6px;
    padding: 3px 8px;
    font-size: 12px;
    font-weight: bold;
    background: #fff1eb;
    color: #c24100;
    &.approved {
      background: #eaf9de;
      color: #008a00;
    }
  }
}

.cc-label-name {
  font-weight: bolder;
  display: block;
  margin-bottom: 0px;
}
</style>
